import { useQuery } from "@tanstack/react-query";
import { useApi } from "./api-context";
import { TaxApiError } from "./errors";
import type { InsightFeed } from "./insights";

export function isInsightsUnavailable(error: unknown): boolean {
  return error instanceof TaxApiError && (error.status === 404 || error.status === 501);
}

function isInsightsAvailable(error: unknown, feed: InsightFeed | undefined): boolean {
  if (isInsightsUnavailable(error)) return false;
  if (error) return feed !== undefined;
  return true;
}

/**
 * Header insights feed. A missing endpoint (404/501) hides the indicator;
 * other failures keep showing only if a prior successful feed is cached.
 */
export function useInsightsFeed(options?: { enabled?: boolean }): {
  available: boolean;
  feed: InsightFeed | undefined;
  isPending: boolean;
  refetch: () => void;
} {
  const api = useApi();
  const query = useQuery({
    queryKey: ["insights", "feed"],
    queryFn: ({ signal }) => api.listInsights(signal),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    refetchInterval: (state) =>
      isInsightsAvailable(state.state.error, state.state.data) ? 120_000 : false,
    refetchOnWindowFocus: true,
    retry: (count, error) => !isInsightsUnavailable(error) && count < 1,
  });

  const feed = query.data;
  const available = isInsightsAvailable(query.error, feed);

  return {
    available,
    feed,
    isPending: query.isPending,
    refetch: () => {
      void query.refetch();
    },
  };
}
